import { useEffect, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import * as endpoints from "@/api/endpoints";
import { apiErrorCode, apiErrorMessage } from "@/api/client";
import { useAuth } from "@/auth/AuthContext";
import { useCart } from "@/cart/CartContext";
import { formatYen } from "@/utils/format";

/**
 * 発注確認画面（off-session 決済）。
 *
 * 顧客は事前に Stripe Billing Portal で「決済用カード」を登録しておく。
 * 発注確定時はそのカードに対して自動課金（顧客側で改めてカード情報を入力する必要なし）。
 * カード未登録時はアカウント設定 → Billing Portal への導線を出す。
 *
 * 3D セキュア（追加認証）について:
 *   off-session 課金で 3DS が要求されると Stripe は失敗扱いを返す。
 *   このとき顧客には「アカウント設定でカードを再登録してください」と案内する。
 */
export function CheckoutPage() {
  const { user } = useAuth();
  const { cart, refresh } = useCart();
  const navigate = useNavigate();

  const [paymentMethod, setPaymentMethod] =
    useState<endpoints.PaymentMethodInfo | null>(null);
  const [paymentMethodLoading, setPaymentMethodLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // 既定カードを取得
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const pm = await endpoints.fetchPaymentMethod();
        if (!cancelled) setPaymentMethod(pm);
      } catch (err) {
        if (!cancelled) {
          setError(apiErrorMessage(err, "カード情報の取得に失敗しました"));
        }
      } finally {
        if (!cancelled) setPaymentMethodLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user]);

  if (cart.items.length === 0 && !submitting) {
    return <Navigate to="/cart" replace />;
  }

  const cardRegistered = paymentMethod?.registered === true;

  const onSubmit = async () => {
    setError(null);
    setSubmitting(true);
    try {
      const order = await endpoints.createOrder({
        items: cart.items.map((i) => ({
          product_id: i.product_id,
          quantity: i.quantity,
        })),
        // 配送先は LOGEX 固定、決済は登録済カードに対する off-session
      });
      await refresh();
      navigate(`/orders/${order.id}`, {
        replace: true,
        state: { justCreated: true },
      });
    } catch (err) {
      const code = apiErrorCode(err);
      if (code === "PAYMENT_METHOD_NOT_REGISTERED") {
        setError(
          "決済用カードが登録されていません。アカウント設定からカード登録をお願いします。",
        );
      } else if (code === "PAYMENT_REQUIRES_ACTION") {
        setError(
          "登録済カードで追加認証（3Dセキュア）が必要となりました。アカウント設定からカードを再登録してください。",
        );
      } else if (code === "PAYMENT_FAILED") {
        setError(
          `決済に失敗しました: ${apiErrorMessage(err)}。アカウント設定からカード情報をご確認ください。`,
        );
      } else if (code === "PAYMENT_PAST_DUE") {
        setError(
          "サブスクリプションのお支払いに失敗しているため、新規発注を受け付けできません。カード情報の更新後に再度お試しください。",
        );
      } else if (code === "SUBSCRIPTION_CANCELLED") {
        setError(
          "サブスクリプションが解約されているため、発注を受け付けできません。お問い合わせください。",
        );
      } else if (code === "INSUFFICIENT_STOCK") {
        setError("在庫が不足しているため発注できません。カートを見直してください。");
      } else {
        setError(apiErrorMessage(err, "発注に失敗しました"));
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6 max-w-3xl mx-auto">
      <h1 className="text-2xl font-semibold">発注内容の確認</h1>

      <div className="card p-4">
        <h2 className="font-semibold mb-3">注文内容</h2>
        <div className="divide-y">
          {cart.items.map((item) => (
            <div key={item.id} className="py-2 flex items-center text-sm">
              <div className="flex-1">
                <div>{item.product?.product_name ?? "(商品)"}</div>
                <div className="text-xs text-gray-500">
                  {item.product?.jan} / 単価 {formatYen(item.tax_inclusive_unit_price ?? 0)} (税込) × {item.quantity}
                </div>
              </div>
              <div className="font-medium">{formatYen(item.subtotal)}</div>
            </div>
          ))}
        </div>
        <div className="flex justify-between text-sm pt-3 mt-3 border-t">
          <span className="text-gray-600">商品代金 (税込)</span>
          <span>{formatYen(cart.total_amount)}</span>
        </div>
        <div className="flex justify-between text-sm mt-2">
          <span className="text-gray-600">送料</span>
          <span className="text-green-700 font-medium">無料</span>
        </div>
        <div className="flex justify-between items-baseline pt-3 mt-3 border-t">
          <span className="text-gray-600">合計 (税込)</span>
          <span className="text-2xl font-bold">{formatYen(cart.total_amount)}</span>
        </div>
      </div>

      <div className="card p-4">
        <h2 className="font-semibold mb-3">配送先</h2>
        <div className="rounded-md bg-blue-50 border border-blue-200 px-3 py-2 text-sm text-blue-900">
          <div className="font-semibold">株式会社 LOGEX 宛</div>
          <div className="text-xs mt-1 text-blue-800">
            弊社指定の物流拠点で一括受取となります。
            個別の発送先指定はご利用いただけません。
          </div>
        </div>
      </div>

      <div className="card p-4">
        <h2 className="font-semibold mb-3">お支払い方法</h2>
        {paymentMethodLoading ? (
          <div className="text-sm text-gray-500">カード情報を取得中...</div>
        ) : cardRegistered ? (
          <div className="rounded-md bg-green-50 border border-green-200 px-3 py-3 text-sm">
            <div className="flex items-baseline justify-between">
              <div>
                <span className="font-semibold uppercase">
                  {paymentMethod?.brand ?? "card"}
                </span>{" "}
                <span className="text-gray-600">**** **** ****</span>{" "}
                <span className="font-mono font-bold">{paymentMethod?.last4}</span>
              </div>
              {paymentMethod?.exp_month && paymentMethod?.exp_year && (
                <span className="text-xs text-gray-500">
                  有効期限 {String(paymentMethod.exp_month).padStart(2, "0")}/
                  {String(paymentMethod.exp_year).slice(-2)}
                </span>
              )}
            </div>
            <div className="text-xs text-gray-600 mt-2">
              上記カードに自動課金されます。カード変更は{" "}
              <Link to="/account" className="text-brand-700 underline">
                アカウント設定
              </Link>{" "}
              から。
            </div>
          </div>
        ) : (
          <div className="rounded-md bg-yellow-50 border border-yellow-200 px-3 py-3 text-sm space-y-2">
            <div className="font-semibold text-yellow-900">
              ⚠️ 決済用カードが登録されていません
            </div>
            <div className="text-xs text-yellow-800">
              発注確定にはカード登録が必要です。アカウント設定の
              「カード登録・請求履歴（Stripe ポータル）」からご登録ください。
            </div>
            <Link
              to="/account"
              className="inline-block btn-primary text-xs mt-1"
            >
              アカウント設定へ
            </Link>
          </div>
        )}
      </div>

      {error && (
        <div className="rounded-md bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-800">
          {error}
        </div>
      )}

      <div className="flex justify-between gap-3">
        <button
          type="button"
          className="btn-secondary"
          onClick={() => navigate("/cart")}
          disabled={submitting}
        >
          カートに戻る
        </button>
        <button
          type="button"
          className="btn-primary"
          onClick={onSubmit}
          disabled={submitting || paymentMethodLoading || !cardRegistered}
        >
          {submitting
            ? "決済処理中..."
            : `${formatYen(cart.total_amount)} で発注を確定`}
        </button>
      </div>
    </div>
  );
}
